import { giphyDarkestGrey } from '@giphy/js-brand'
import { useContext, useEffect, useState } from 'react'
import styled, { css } from 'styled-components'
import { BORDER_RADIUS_MEDIUM } from '../../constants'
import { CreationToolsContext } from '../../manager'
import CreationToolGradient from '../../types/gradient'
import CreationToolSourceEditor from '../../types/source-editor'
import { TitleSmall } from '../typography'
import { CreationSourceEditorContext } from './manager'

const Container = styled.div`
    flex-shrink: 0;
    width: 175px;
`

const Label = styled(TitleSmall)`
    margin: 0;
`

const Icon = styled.div`
    align-items: center;
    background: ${giphyDarkestGrey};
    border-radius: ${BORDER_RADIUS_MEDIUM}px;
    display: flex;
    font-size: 20px;
    height: 50px;
    justify-content: center;
    margin-right: 12px;
    transition: background-color 0.2s ease-out;
    user-select: none;
    width: 50px;

    svg {
        height: 30px;
        width: 30px;
    }
`

const Item = styled.div<{ colors: string[]; isActive: boolean }>`
    align-items: center;
    color: #fff;
    cursor: pointer;
    display: flex;
    margin-bottom: 10px;

    ${Label} {
        opacity: 0;
        transition: opacity 0.2s ease-out;
    }

    &:hover ${Label} {
        opacity: 1;
    }

    ${({ colors, isActive }) =>
        isActive &&
        css`
            ${Icon} {
                background: linear-gradient(45deg, ${colors[0]} 0%, ${colors[1]} 100%);
            }

            svg {
                filter: brightness(5) drop-shadow(0 2px 5px rgba(0, 0, 0, 0.33));
            }
        `}
`

const CreationSourceEditorNavigation = () => {
    const { tool } = useContext(CreationToolsContext)
    const { currentEditor, outputType, setCurrentEditor, source } = useContext(CreationSourceEditorContext)
    const [availableEditors, setAvailableEditors] = useState<CreationToolSourceEditor[]>([])

    useEffect(() => {
        const editors = CreationToolSourceEditor.all().filter((editor) => {
            const supportedOutputTypes = CreationToolSourceEditor.supportedOutputTypes(editor)
            const supportedTools = CreationToolSourceEditor.supportedCreationTools(editor)
            const supportedEditors = CreationToolSourceEditor.supportedToolsForSource(source)

            return (
                supportedTools.indexOf(tool) > -1 &&
                supportedOutputTypes.indexOf(outputType) > -1 &&
                supportedEditors.indexOf(editor) > -1
            )
        })
        setAvailableEditors(editors)
    }, [outputType])

    return (
        <Container>
            {availableEditors.map((editor) => {
                const EditorIcon = CreationToolSourceEditor.icon(editor)
                const gradient = CreationToolSourceEditor.gradient(editor)
                const colors = CreationToolGradient.colors(gradient)
                const isActive = editor === currentEditor
                return (
                    <Item
                        key={`source-editor-nav-${editor}`}
                        {...{
                            colors,
                            isActive,
                            onClick: () => setCurrentEditor(editor),
                        }}
                    >
                        <Icon>{<EditorIcon colors={colors} />}</Icon>
                        <Label>{CreationToolSourceEditor.title(editor)}</Label>
                    </Item>
                )
            })}
        </Container>
    )
}

export default CreationSourceEditorNavigation
